import {
  useArchiveConversationMutation,
  useSeenConversationMutation,
} from '$/GraphQL/schema/schema';
import {
  SocketMessageResponse,
  ConversationFragment,
  ConversationTypeFilter,
  AttendeeFragment,
} from '$/GraphQL/schema/types';
import isMobile from '$/utils/isMobile';
import React, { useState } from 'react';
import { MessageInList } from './types';
import { navigate } from '@reach/router';
import { MessageDraft, MessageDraftDetail } from '../BrowserStorage';
import { useMessageStorage } from '$/hooks/useMessageStorage';
import { useApolloClient } from '@apollo/client';
import { CONVERSATION_FRAGMENT, GET_CONVERSATIONS } from '$/GraphQL/queries/Conversation';
import gql from 'graphql-tag';
import { logger } from '$/utils/logger';
import { useServerContext } from '$/hooks/useServerDateNow';

interface ChatContextInfo {
  serverDateNow: () => Date;
  openChatWindowState: string[] | 'all' | null;
  setOpenChatWindowState: (val: boolean, notificationMessageId?: string) => void;
  countNewMessages: number;
  setCountNewMessages: (val: number) => void;
  newMessageBadge?: SocketMessageResponse | MessageInList;
  setNewMessageBadge: (val?: SocketMessageResponse | MessageInList) => void;
  chatHasFocusRef: React.MutableRefObject<boolean>;
  filterRef: React.RefObject<HTMLDivElement>;
  conversationId?: string;
  setConversationId: (val?: string) => void;
  currentConversation: ConversationFragment | undefined;
  setCurrentConversation: (val: ConversationFragment | undefined) => void;
  isResetCurrentConversation?: boolean;
  setIsResetCurrentConversation: (val: boolean) => void;
  isRefetchConversation: boolean;
  setIsRefetchConversation: (val: boolean) => void;
  linkDashboardChat?: string | null;
  setLinkDashboardChat: (val: string | null) => void;
  refetchConversation: (val: boolean) => void;
  archiveConversation: (conversation: ConversationFragment) => void;
  seenConversation: (val: string) => void;
  listMessagesDraft: MessageDraft;
  updateMessagesDraft: (val?: string) => void;
  messageDraft?: MessageDraftDetail;
  isCapturingUngatedAttendeeInfo: boolean;
  setIsCapturingUngatedAttendeeInfo: (val: boolean) => void;
  isUngatedForm: boolean;
  setIsUngatedForm: (val: boolean) => void;

  keyboardHeight: number; // storing keyboard height on iOS mobile
  setKeyboardHeight: (val: number) => void;
}
interface ChatContextProviderProps {
  setHeightOfAdminSectionMobile?: (val: number) => void;
  conversationId?: string;
  attendee?: AttendeeFragment;
  children?: React.ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const ChatContext = React.createContext<ChatContextInfo>({} as any as ChatContextInfo);

const useChatContextProvider = ({
  setHeightOfAdminSectionMobile,
  conversationId: defaultConversationId,
}: ChatContextProviderProps): ChatContextInfo => {
  const { serverDateNow } = useServerContext();
  // number of new messages, used for attendee view
  const [countNewMessages, setCountNewMessages] = React.useState(0);
  // info of new message
  const [newMessageBadge, setNewMessageBadge] = React.useState<
    SocketMessageResponse | MessageInList
  >();

  // check focus of chat input
  const chatHasFocusRef = React.useRef(false);
  const [openChatWindowState, setOpenChatWindowStateInternal] = useState<string[] | 'all' | null>(
    null
  );
  const setOpenChatWindowState = React.useCallback(
    (open: boolean, notificationMessageId?: string) => {
      setOpenChatWindowStateInternal((currentState) => {
        if (!open) return null;

        if (notificationMessageId) {
          if (Array.isArray(currentState)) return [...currentState, notificationMessageId];
          return [notificationMessageId];
        }
        return 'all';
      });
    },
    []
  );

  const [conversationId, setConversationId] = React.useState<string | undefined>(
    defaultConversationId
  );
  const {
    item: messageDraft,
    add: addMessageDraft,
    remove: removeMessageDraft,
    list: messagesContentFilterOut,
  } = useMessageStorage(conversationId);
  const [listMessagesDraft, setListMessagesDraft] =
    React.useState<MessageDraft>(messagesContentFilterOut);
  const updateMessagesDraft = React.useCallback(
    (message?: string): void => {
      message ? addMessageDraft(message) : removeMessageDraft();
      conversationId &&
        setListMessagesDraft({
          ...listMessagesDraft,
          [conversationId]: message
            ? {
                date: serverDateNow(),
                content: message,
              }
            : undefined,
        });
    },
    [addMessageDraft, conversationId, listMessagesDraft, removeMessageDraft, serverDateNow]
  );
  const filterRef = React.useRef<HTMLDivElement>(null);
  const [currentConversation, setCurrentConversation] = React.useState<
    ConversationFragment | undefined
  >();
  const [isResetCurrentConversation, setIsResetCurrentConversation] = React.useState(false);
  const [isRefetchConversation, setIsRefetchConversation] = React.useState(false);

  const [linkDashboardChat, setLinkDashboardChat] = React.useState<string | null>(null);
  const [isCapturingUngatedAttendeeInfo, setIsCapturingUngatedAttendeeInfo] = React.useState(false);

  const [isUngatedForm, setIsUngatedForm] = React.useState(false);

  const [keyboardHeight, setKeyboardHeight] = React.useState<number>(0);

  const refetchConversation = React.useCallback((resetCurrentConversation: boolean): void => {
    setIsResetCurrentConversation && setIsResetCurrentConversation(resetCurrentConversation);
    setIsRefetchConversation && setIsRefetchConversation(true);
  }, []);

  const client = useApolloClient();

  const updateArchivedConversationCache = React.useCallback(
    (archivedConversation: ConversationFragment): void => {
      try {
        const setId = archivedConversation.set.id;
        const archivedConversationId = archivedConversation.id;

        // Update cache
        client.writeFragment({
          id: `Conversation:${archivedConversationId}`,
          fragment: gql`
            fragment ArchivedConversation on Conversation {
              id
              isArchived
            }
          `,
          data: {
            id: archivedConversationId,
            isArchived: true,
            __typename: 'Conversation',
          },
        });

        const archivedConversationFilters = {
          setId: setId,
          type: ConversationTypeFilter.Archived,
        };

        const conversationCache = client.readQuery({
          query: GET_CONVERSATIONS,
          variables: {
            filters: archivedConversationFilters,
          },
        });
        if (!conversationCache.conversations) return;

        // archived conversation in cache
        const conversation = client.readFragment({
          id: `Conversation:${archivedConversationId}`,
          fragment: CONVERSATION_FRAGMENT,
          fragmentName: 'Conversation',
        });
        if (!conversation) return;

        const cachedConversations = {
          ...conversationCache.conversations,
          conversations: [...conversationCache.conversations.conversations, conversation],
        };

        client.writeQuery({
          query: GET_CONVERSATIONS,
          variables: {
            filters: archivedConversationFilters,
          },
          data: { conversations: cachedConversations },
        });
      } catch (error) {
        logger.error('CHAT', 'updateArchivedConversationCache -> ERROR: ', error);
      }
    },
    [client]
  );

  // archive conversation
  const [archiveConversationMutation] = useArchiveConversationMutation();
  const archiveConversation = React.useCallback(
    async (conversation: ConversationFragment): Promise<void> => {
      try {
        await archiveConversationMutation({
          variables: { id: conversation.id },
        });

        // update archived conversation list
        updateArchivedConversationCache(conversation);

        if (conversation.id === currentConversation?.id) {
          linkDashboardChat && navigate(linkDashboardChat);
        }
      } catch (e) {
        console.error(e);
      }
    },
    [
      archiveConversationMutation,
      currentConversation?.id,
      linkDashboardChat,
      updateArchivedConversationCache,
    ]
  );

  //mark as read
  const [seenConversationMutation] = useSeenConversationMutation();
  const seenConversation = React.useCallback(
    async (id: string): Promise<void> => {
      try {
        await seenConversationMutation({
          variables: {
            id,
          },
        });
      } catch (e) {
        console.error(e);
      }
    },
    [seenConversationMutation]
  );

  React.useEffect(() => {
    if (isMobile) {
      const heightOfAdminSection = (100 * window.innerHeight) / 100;
      setHeightOfAdminSectionMobile && setHeightOfAdminSectionMobile(heightOfAdminSection);
    }
  }, [openChatWindowState, setHeightOfAdminSectionMobile]);

  const chatContextProvider: ChatContextInfo = {
    serverDateNow,
    openChatWindowState,
    setOpenChatWindowState,
    countNewMessages,
    setCountNewMessages,
    newMessageBadge,
    setNewMessageBadge,
    chatHasFocusRef,
    filterRef,
    conversationId,
    setConversationId,
    currentConversation,
    setCurrentConversation,
    isResetCurrentConversation,
    setIsResetCurrentConversation,
    isRefetchConversation,
    setIsRefetchConversation,
    linkDashboardChat,
    setLinkDashboardChat,
    refetchConversation,
    archiveConversation,
    seenConversation,
    listMessagesDraft,
    updateMessagesDraft,
    messageDraft,
    isCapturingUngatedAttendeeInfo,
    setIsCapturingUngatedAttendeeInfo,
    isUngatedForm,
    setIsUngatedForm,
    keyboardHeight,
    setKeyboardHeight,
  };
  return chatContextProvider;
};

const ChatContextProvider: React.FC<ChatContextProviderProps> = ({
  children,
  conversationId,
  setHeightOfAdminSectionMobile,
  attendee,
}) => {
  const chatContextProvider = useChatContextProvider({
    setHeightOfAdminSectionMobile,
    conversationId,
    attendee,
  });

  return <ChatContext.Provider value={chatContextProvider}>{children}</ChatContext.Provider>;
};

export default ChatContextProvider;

export const useChatContext = (): ChatContextInfo => {
  return React.useContext<ChatContextInfo>(ChatContext);
};
